/**
 * CommentsForm - Component for rendering and handling comment forms
 * 
 * This module provides functionality to render a comment form and
 * attach submit events, using the CommentsPoster module for submissions.
 */

import { commentsFormBuilder } from './CommentsFormBuilder.js';
import { submitComment, validateComment } from './CommentsPoster.js';
import { modalBase } from './ModalBase.js';

/**
 * Create and render a comment form
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.placeholderSelector - CSS selector for the placeholder element
 * @param {number} options.postId - The post ID to comment on
 * @param {Object} options.labels - Form labels and messages
 * @param {string} options.labels.submitButton - Text for the submit button
 * @param {string} options.labels.placeholder - Placeholder text for the textarea
 * @param {number|string} options.parentId - ID of parent comment when replying
 * @param {Function} options.onSuccess - Callback for successful submission
 * @param {Function} options.onError - Callback for error handling
 * @param {Function} options.auth - Auth status
 * @returns {Object} - Form controller with methods to interact with the form
 */
export const createCommentsForm = (options = {}) => {
  const {
    placeholderSelector = '.po-comments-form__placeholder',
    postId = 0,
    parentId = 0,
    labels = {
      submitButton: 'Submit',
      placeholder: 'Write your comment here...',
    },
    onSuccess = () => {},
    onError = () => {},
  } = options;

  // Find the placeholder element
  const placeholderElement = document.querySelector(placeholderSelector);
  if (!placeholderElement) {
    console.error('Comment form placeholder element not found');
    return null;
  }

  const formAPI = commentsFormBuilder({
    ...options,
    parentId
  });
  
  // Replace placeholder with the form
  placeholderElement.innerHTML = '';
  placeholderElement.appendChild(formAPI.form);
  
  // Set initial state
  let isSubmitting = false;
  
  /**
   * Handle form submission
   */
  const handleSubmit = async (event) => {
    // Ensure default form submission is prevented
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    if (isSubmitting) {
      return;
    }
    
    const content = formAPI.getComment();
    
    if (!validateComment(content)) {
      onError(new Error('Please enter a valid comment'));
      return;
    }
    
    isSubmitting = true;
    formAPI.submitButton.disabled = true;
    
    try {
      const result = await submitComment(postId, content, {
        onSuccess,
        onError,
        parentId
      });
      
      // Clear the textarea on success
      formAPI.reset()
      
      return result;
    } catch (error) {
      console.error('Failed to submit comment:', error);
      formAPI.submitButton.disabled = false;
      // Error is handled by the onError callback
    } finally {
      isSubmitting = false;
    }
  };
  
  // Attach event listener to form
  formAPI.form.addEventListener('submit', (e) => {
    handleSubmit(e);
    // Double-check that we're preventing default behavior
    if (!e.defaultPrevented) {
      console.warn('Default form behavior not prevented! Preventing now.');
      e.preventDefault();
    }
    return false; // Extra safeguard
  });
  
  /**
   * Reset the form to its initial state
   */
  const reset = () => {
    formAPI.reset()
    isSubmitting = false;
  };
  
  /**
   * Remove the form and clean up event listeners
   */
  const destroy = () => {
    formAPI.form.removeEventListener('submit', handleSubmit);
    if (placeholderElement.contains(formAPI.form)) {
      placeholderElement.removeChild(formAPI.form);
    }
  };
  
  return {
    form: formAPI.form,
    submitButton: formAPI.submitButton,
    reset,
    destroy,
  };
};

/**
 * Initialize the comments form for the current page
 * 
 * @param {Object} options - Configuration options
 * @returns {Object|null} - Form controller or null if initialization failed
 */
export const initCommentsForm = (options = {}) => {
  // Get post ID from body class (postid-XXXXX)
  let postId = 0;
  const bodyClasses = document.body.className.split(' ');
  const postIdClass = bodyClasses.find(className => className.startsWith('postid-'));
  
  if (postIdClass) {
    postId = parseInt(postIdClass.replace('postid-', ''), 10);
  }
  
  if (!postId) {
    console.error('Could not determine post ID for comments from body class');
    return null;
  }
  
  const defaultOptions = {
    postId,
    onSuccess: (data) => {
      // Show success message or update UI
      console.log('Comment submitted successfully', data);
    },
    onError: (error) => {
      // Show error message
      console.error('Comment submission error', error);
    },
  };
  
  return createCommentsForm({
    ...defaultOptions,
    ...options,
  });
};

/**
 * Initialize reply functionality for comments
 * 
 * This function attaches click event listeners to all .po-comment__reply elements
 * and creates a comment form under the clicked comment
 * @param {Function} options.auth - Auth status
 */
export const initCommentReplies = (options = {}) => {
  let activeReplyForm = null;
  
  // Find all reply buttons
  const replyButtons = document.querySelectorAll('.po-comment__reply');
  
  replyButtons.forEach(replyButton => {
    replyButton.addEventListener('click', (event) => {
      event.preventDefault();
      
      // Find the comment element
      const commentElement = replyButton.closest('.po-comment');
      if (!commentElement) return;
      
      // Get the comment ID from the comment element id (format: comment-123)
      const commentId = commentElement.id.replace('comment-', '');
      if (!commentId) return;
      
      // Remove any existing reply forms
      if (activeReplyForm) {
        activeReplyForm.destroy();
        activeReplyForm = null;
      }
      
      // Create a placeholder for the reply form
      let replyPlaceholder = commentElement.querySelector('.po-comment-reply-form');
      if (!replyPlaceholder) {
        replyPlaceholder = document.createElement('div');
        replyPlaceholder.className = 'po-comment-reply-form';
        
        // Find the correct location to append the form
        // First try to find po-comment__replies, if not found append to po-comment
        const repliesContainer = commentElement.querySelector('.po-comment__replies');
        if (repliesContainer) {
          repliesContainer.prepend(replyPlaceholder);
        } else {
          commentElement.appendChild(replyPlaceholder);
        }
      }
      
      // Get post ID from body class
      let postId = 0;
      const bodyClasses = document.body.className.split(' ');
      const postIdClass = bodyClasses.find(className => className.startsWith('postid-'));
      
      if (postIdClass) {
        postId = parseInt(postIdClass.replace('postid-', ''), 10);
      }
      
      if (!postId) {
        console.error('Could not determine post ID for comments from body class');
        return;
      }
      
      // Create the reply form
      activeReplyForm = createCommentsForm({
        placeholderSelector: '.po-comment-reply-form',
        postId: postId,
        parentId: commentId,
        labels: {
          submitButton: 'Reply',
          placeholder: 'Write your reply here...',
        },
        auth: options.auth,
        onSuccess: (data) => {
          console.log('Reply submitted successfully', data);
          
          // Remove the reply form after successful submission
          activeReplyForm.destroy();
          activeReplyForm = null;

          modalBase(
            "Your comment has been submitted. ",
            "Once approved, it will appear in the comment section below this article.", 
            "ok"
          )
          
          // Optionally, refresh the comments section to show the new reply
          // This can be implemented based on how your site refreshes comments
        },
        onError: (error) => {
          console.error('Reply submission error', error);
        }
      });
    });
  });
};

export default {
  createCommentsForm,
  initCommentsForm,
  initCommentReplies
};
